/**
 * TrialBanner Component
 * 
 * Displays trial status with days remaining and upgrade prompt.
 * Shows prominently in the dashboard for trial users.
 */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Typography,
    Button,
    LinearProgress,
    Chip,
    alpha,
    IconButton,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';

interface TrialBannerProps {
    trialDaysRemaining: number;
    trialTotalDays?: number;
    planName?: string;
    planTier?: 'basic' | 'pro' | 'elite' | 'starter' | 'growth' | 'enterprise';
    planLimits?: {
        vehicles: number;
        users: number;
        branches: number;
    };
    onUpgrade?: () => void;
    onDismiss?: () => void;
    compact?: boolean;
    variant?: 'banner' | 'modal';
}
const TrialBanner: React.FC<TrialBannerProps> = ({
    trialDaysRemaining,
    trialTotalDays = 30,
    planName = 'Drive Pro',
    planTier = 'pro',
    planLimits,
    onUpgrade,
    onDismiss,
    compact = false,
    variant = 'banner',
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [hasSeenWelcome, setHasSeenWelcome] = useState(false);
    const [dismissed, setDismissed] = useState(false);
    const resolvedTrialTotalDays = Math.max(trialTotalDays || trialDaysRemaining || 1, 1);

    // Check if welcome message has been shown before
    useEffect(() => {
        const welcomeShown = localStorage.getItem(`trial_welcome_shown_${resolvedTrialTotalDays}`);
        setHasSeenWelcome(welcomeShown === 'true');

        if (variant !== 'modal') {
            const bannerDismissed = localStorage.getItem(`trial_banner_dismissed_${trialDaysRemaining}`);
            setDismissed(bannerDismissed === 'true');
        } else {
            setDismissed(false);
        }
    }, [resolvedTrialTotalDays, trialDaysRemaining, variant]);

    // Mark welcome as shown when component mounts
    useEffect(() => {
        if (!hasSeenWelcome && trialDaysRemaining === resolvedTrialTotalDays) {
            localStorage.setItem(`trial_welcome_shown_${resolvedTrialTotalDays}`, 'true');
            setHasSeenWelcome(true);
        }
    }, [hasSeenWelcome, trialDaysRemaining, resolvedTrialTotalDays]);

    // Get plan display info
    const getPlanInfo = () => {
        const plans: Record<string, { name: string; emoji: string }> = {
            basic: { name: 'Drive Basic', emoji: '🚗' },
            pro: { name: 'Drive Pro', emoji: '🚘' },
            elite: { name: 'Drive Elite', emoji: '🚔' },
            starter: { name: 'Starter', emoji: '🚗' },
            growth: { name: 'Drive Pro', emoji: '🚘' },
            enterprise: { name: 'Fleet Enterprise', emoji: '🚔' },
        };
        const fallback = { name: planName || plans.pro.name, emoji: '🚘' };
        return plans[planTier] || fallback;
    };

    const planInfo = getPlanInfo();

    // Calculate progress (inverse - shows remaining)
    const daysUsed = resolvedTrialTotalDays - trialDaysRemaining;
    const progressPercent = Math.min(100, (daysUsed / resolvedTrialTotalDays) * 100);

    // Determine urgency level
    const getUrgencyLevel = () => {
        if (trialDaysRemaining <= 3) return 'critical';
        if (trialDaysRemaining <= 7) return 'warning';
        return 'normal';
    };

    const urgency = getUrgencyLevel();

    // Get colors based on urgency
    const getColors = () => {
        switch (urgency) {
            case 'critical':
                return {
                    bg: alpha('#C8102E', 0.08),
                    border: '#C8102E',
                    progressBg: alpha('#C8102E', 0.2),
                    progress: '#C8102E',
                    text: '#8B0A1F',
                    badge: '#C8102E',
                    badgeText: '#FFFFFF',
                };
            case 'warning':
                return {
                    bg: alpha('#FF690F', 0.08),
                    border: '#FF690F',
                    progressBg: alpha('#FF690F', 0.2),
                    progress: '#FF690F',
                    text: '#B54708',
                    badge: '#FF690F',
                    badgeText: '#FFFFFF',
                };
            default:
                return {
                    bg: alpha('#5EBD81', 0.08),
                    border: '#5EBD81',
                    progressBg: alpha('#5EBD81', 0.2),
                    progress: '#5EBD81',
                    text: '#2E7D32',
                    badge: '#5EBD81',
                    badgeText: '#FFFFFF',
                };
        }
    };

    const colors = getColors();

    const handleUpgradeClick = () => {
        if (onUpgrade) {
            onUpgrade();
        } else {
            navigate('/pricing');
        }
    };

    const handleDismiss = () => {
        if (onDismiss) {
            onDismiss();
            return;
        }
        setDismissed(true);
        localStorage.setItem(`trial_banner_dismissed_${trialDaysRemaining}`, 'true');
    };

    // Determine if this is first-time trial (show welcome) or ongoing
    const isNewTrial = trialDaysRemaining === resolvedTrialTotalDays && !hasSeenWelcome;
    const showWelcome = isNewTrial;

    if (dismissed) {
        return null;
    }

    // Compact version for sidebar or header
    if (compact) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: colors.bg,
                    border: `1px solid ${colors.border}`,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                        bgcolor: alpha(colors.border, 0.15),
                    },
                }}
                onClick={handleUpgradeClick}
            >
                <Typography variant="body2" fontWeight={600} sx={{ color: colors.text }}>
                    {t('subscription.trialBanner.daysLeft', { count: trialDaysRemaining })}
                </Typography>
                <Chip
                    label={t('subscription.trialBanner.upgrade')}
                    size="small"
                    sx={{
                        ml: 'auto',
                        height: 22,
                        bgcolor: colors.border,
                        color: '#fff',
                        fontWeight: 600,
                        fontSize: 11,
                    }}
                />
            </Box>
        );
    }

    return (
        <motion.div
            initial={{ opacity: 0, scale: variant === 'modal' ? 0.95 : 1 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
        >
            <Box
                sx={{
                    bgcolor: variant === 'modal' ? 'rgba(255, 255, 255, 0.85)' : colors.bg,
                    backdropFilter: variant === 'modal' ? 'blur(16px)' : 'none',
                    border: variant === 'modal' ? '1px solid rgba(255, 255, 255, 0.3)' : `2px solid ${colors.border}`,
                    borderRadius: variant === 'modal' ? 4 : 3,
                    p: variant === 'modal' ? 5 : 2,
                    mb: variant === 'modal' ? 0 : 2,
                    position: 'relative',
                    overflow: 'hidden',
                    boxShadow: variant === 'modal' ? '0 20px 60px rgba(0,0,0,0.1)' : 'none',
                }}
            >
                <IconButton
                    aria-label={t('subscription.trialBanner.close')}
                    size="small"
                    onClick={handleDismiss}
                    sx={{
                        position: 'absolute',
                        top: 10,
                        right: 10,
                        bgcolor: alpha(colors.border, 0.12),
                        color: colors.text,
                        boxShadow: `0 2px 6px ${alpha(colors.border, 0.25)}`,
                        '&:hover': {
                            bgcolor: alpha(colors.border, 0.2),
                        },
                        zIndex: 2,
                    }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
                {/* Background Pattern */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        width: '150px',
                        height: '150px',
                        opacity: 0.05,
                        background: `radial-gradient(circle, ${colors.border} 20%, transparent 20%)`,
                        backgroundSize: '15px 15px',
                    }}
                />

                {/* Header Section */}
                <Box sx={{ textAlign: 'center', mb: 1.5, position: 'relative', zIndex: 1 }}>
                    <Chip
                        label={t('subscription.trialBanner.planTag', { planName: planInfo.name, trialTotalDays: resolvedTrialTotalDays })}
                        sx={{
                            bgcolor: colors.badge,
                            color: colors.badgeText,
                            fontWeight: 700,
                            fontSize: '0.8rem',
                            height: 29,
                            mb: 1,
                        }}
                    />
                    <Typography
                        variant="h5"
                        sx={{
                            fontWeight: 800,
                            color: colors.text,
                            mb: 0.5,
                            letterSpacing: '-0.5px',
                            fontSize: '1.35rem',
                        }}
                    >
                        {showWelcome
                            ? t('subscription.trialBanner.title.welcome')
                            : urgency === 'critical'
                                ? t('subscription.trialBanner.title.critical')
                                : urgency === 'warning'
                                    ? t('subscription.trialBanner.title.warning')
                                    : t('subscription.trialBanner.title.remaining', { count: trialDaysRemaining })
                        }
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            color: colors.text,
                            lineHeight: 1.5,
                            maxWidth: '650px',
                            mx: 'auto',
                            fontSize: '0.8rem',
                        }}
                    >
                        {showWelcome ? (
                            <>{t('subscription.trialBanner.description.welcome', { count: trialDaysRemaining, planName: planInfo.name })}</>
                        ) : urgency === 'critical' ? (
                            <>{t('subscription.trialBanner.description.critical', { count: trialDaysRemaining })}</>
                        ) : urgency === 'warning' ? (
                            <>{t('subscription.trialBanner.description.warning', { count: trialDaysRemaining, planName: planInfo.name })}</>
                        ) : (
                            <>{t('subscription.trialBanner.description.normal', { count: trialDaysRemaining, planName: planInfo.name })}</>
                        )}
                    </Typography>
                </Box>

                {/* Progress Bar */}
                <Box sx={{ mb: 1.5, position: 'relative', zIndex: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, px: 0.5 }}>
                        <Typography variant="caption" sx={{ color: colors.text, fontWeight: 600, fontSize: '0.7rem' }}>
                            {t('subscription.trialBanner.progress')}
                        </Typography>
                        <Typography variant="caption" sx={{ color: colors.text, fontWeight: 700, fontSize: '0.7rem' }}>
                            {t('subscription.trialBanner.progressStatus', { remaining: trialDaysRemaining, total: resolvedTrialTotalDays })}
                        </Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={progressPercent}
                        sx={{
                            height: 8,
                            borderRadius: 4,
                            bgcolor: colors.progressBg,
                            '& .MuiLinearProgress-bar': {
                                bgcolor: colors.progress,
                                borderRadius: 4,
                            },
                        }}
                    />
                </Box>

                {/* Features */}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center', mb: 1.5, position: 'relative', zIndex: 1 }}>
                    <Chip
                        label={
                            planLimits?.vehicles === -1
                                ? t('subscription.trialBanner.features.unlimitedVehicles')
                                : t('subscription.trialBanner.features.vehicles', { count: planLimits?.vehicles || 50 })
                        }
                        size="small"
                        sx={{
                            bgcolor: alpha(colors.border, 0.1),
                            color: colors.text,
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            height: 24,
                            border: `1px solid ${alpha(colors.border, 0.3)}`,
                        }}
                    />
                    <Chip
                        label={
                            planLimits?.users === -1
                                ? t('subscription.trialBanner.features.unlimitedUsers')
                                : t('subscription.trialBanner.features.users', { count: planLimits?.users || 5 })
                        }
                        size="small"
                        sx={{
                            bgcolor: alpha(colors.border, 0.1),
                            color: colors.text,
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            height: 24,
                            border: `1px solid ${alpha(colors.border, 0.3)}`,
                        }}
                    />
                    <Chip
                        label={
                            planTier === 'enterprise' || planTier === 'elite'
                                ? t('subscription.trialBanner.features.customSolutions')
                                : t('subscription.trialBanner.features.advancedAnalytics')
                        }
                        size="small"
                        sx={{
                            bgcolor: alpha(colors.border, 0.1),
                            color: colors.text,
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            height: 24,
                            border: `1px solid ${alpha(colors.border, 0.3)}`,
                        }}
                    />
                </Box>

                {/* Centered Upgrade Button */}
                <Box sx={{ display: 'flex', justifyContent: 'center', position: 'relative', zIndex: 1 }}>
                    <Button
                        variant="contained"
                        size="large"
                        onClick={handleUpgradeClick}
                        sx={{
                            bgcolor: colors.border,
                            color: '#fff',
                            fontWeight: 700,
                            fontSize: '1rem',
                            px: 5,
                            py: 1.4,
                            borderRadius: 2,
                            textTransform: 'none',
                            boxShadow: `0 4px 14px ${alpha(colors.border, 0.3)}`,
                            '&:hover': {
                                bgcolor: colors.text,
                                boxShadow: `0 6px 20px ${alpha(colors.border, 0.4)}`,
                                transform: 'translateY(-2px)',
                            },
                            transition: 'all 0.2s ease',
                        }}
                    >
                        {t('subscription.trialBanner.upgradeNow')}
                    </Button>
                </Box>
            </Box>
        </motion.div>
    );
};

export default TrialBanner;
